// Helpers for the per-song reference videos stored in a MusicXML file's
// .meta.json sidecar (written by api/musicxml.js update-metadata).
// Entry shape: { url, title, notes }
import { extractYouTubeId, extractVimeoId } from './videoEmbed';

export const MAX_SONG_VIDEOS = 12;
export const MAX_VIDEO_TITLE_LENGTH = 200;
export const MAX_VIDEO_NOTES_LENGTH = 1000;

// Provider IDs end up inside an iframe src, so accept only the character sets
// the providers actually use rather than whatever the URL happened to contain.
const YOUTUBE_ID_PATTERN = /^[A-Za-z0-9_-]{6,20}$/;
const VIMEO_ID_PATTERN = /^\d{6,15}$/;

export const isSafeHttpUrl = (url) => {
  if (typeof url !== 'string' || !url.trim()) return false;
  try {
    const { protocol } = new URL(url.trim());
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * Resolve a raw URL into { provider, id }. provider is null when the link
 * isn't something we can embed — the caller falls back to a plain link.
 */
export const parseVideoUrl = (url) => {
  if (!isSafeHttpUrl(url)) return { provider: null, id: null };
  const trimmed = url.trim();

  const youtubeId = extractYouTubeId(trimmed);
  if (youtubeId && YOUTUBE_ID_PATTERN.test(youtubeId)) {
    return { provider: 'youtube', id: youtubeId };
  }

  const vimeoId = extractVimeoId(trimmed);
  if (vimeoId && VIMEO_ID_PATTERN.test(vimeoId)) {
    return { provider: 'vimeo', id: vimeoId };
  }

  return { provider: null, id: null };
};

export const getEmbedUrl = ({ provider, id }) => {
  // autoplay is safe here: the iframe is only created once the user clicks play
  if (provider === 'youtube') {
    return `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`;
  }
  if (provider === 'vimeo') {
    return `https://player.vimeo.com/video/${id}?autoplay=1`;
  }
  return null;
};

// Vimeo thumbnails need an API call, so only YouTube gets a preview image.
export const getThumbnailUrl = ({ provider, id }) =>
  provider === 'youtube' ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : null;

/**
 * Drop malformed entries and clamp lengths. Applied when reading metadata and
 * again before saving, so a hand-edited or legacy sidecar can't break the UI.
 */
export const normalizeVideos = (raw) => {
  if (!Array.isArray(raw)) return [];

  const normalized = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;

    const url = typeof entry.url === 'string' ? entry.url.trim() : '';
    if (!isSafeHttpUrl(url)) continue;

    normalized.push({
      url,
      title: typeof entry.title === 'string'
        ? entry.title.trim().slice(0, MAX_VIDEO_TITLE_LENGTH)
        : '',
      notes: typeof entry.notes === 'string'
        ? entry.notes.slice(0, MAX_VIDEO_NOTES_LENGTH)
        : ''
    });

    if (normalized.length >= MAX_SONG_VIDEOS) break;
  }
  return normalized;
};
